<template>
  <div class="verify-page login__bg">
    <div class="verify-content">
      <!-- Logo -->
      <div class="logo-section">
        <img
          :src="logoSrc"
          alt="ConCura"
          class="brand-logo"
        />
      </div>

      <!-- Loading -->
      <div v-if="loading" class="verify-card loading-card">
        <b-spinner variant="secondary" :label="$t('caricamento')"></b-spinner>
        <p class="mt-3 text-muted mb-0">{{ $t("SubscriptionVerify.caricamento") }}</p>
      </div>

      <!-- Card risultato -->
      <div v-else class="verify-card" :class="statusClass">
        <!-- Icona stato -->
        <div class="status-icon" :class="statusClass">
          <b-icon :icon="statusIcon" font-scale="2.5"></b-icon>
        </div>

        <!-- Titolo e sottotitolo -->
        <h1 class="status-title">{{ statusTitle }}</h1>
        <p class="status-subtitle">{{ statusSubtitle }}</p>

        <!-- Dettagli utente -->
        <div v-if="verificationData && verificationData.status !== 'not_found'" class="details-section">
          <div class="detail-row">
            <span class="detail-label">{{ $t("SubscriptionVerify.intestatario") }}</span>
            <span class="detail-value">{{ verificationData.user.name }} {{ verificationData.user.surname }}</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">{{ $t("SubscriptionVerify.piano") }}</span>
            <span class="detail-value">{{ verificationData.subscription.name }}</span>
          </div>
          <div v-if="verificationData.status === 'valid' && verificationData.subscription.end_date" class="detail-row">
            <span class="detail-label">{{ validityLabel }}</span>
            <span class="detail-value">{{ formatDate(verificationData.subscription.end_date) }}</span>
          </div>
          <div v-if="verificationData.status === 'expired'" class="detail-row">
            <span class="detail-label">{{ $t("SubscriptionVerify.scaduto_il") }}</span>
            <span class="detail-value text-warning">{{ formatDate(verificationData.subscription.end_date) }}</span>
          </div>
        </div>

        <!-- Badge stato -->
        <div class="status-badge" :class="statusClass">
          <b-icon :icon="badgeIcon" class="mr-2"></b-icon>
          {{ badgeText }}
        </div>
      </div>

      <!-- Footer -->
      <p class="footer-text">{{ $t("SubscriptionVerify.footer_info") }}</p>
    </div>
  </div>
</template>

<script>
import config from "@/_config/config";
import axios from "axios";
import moment from "moment";

export default {
  name: "SubscriptionVerify",
  data() {
    return {
      loading: true,
      verificationData: null,
    };
  },
  computed: {
    logoSrc() {
      const scope = this.$root.scope || process.env.VUE_APP_SCOPE || "concura";
      return require(`@/assets/${scope}/logo_area_pubblica_reg.png`);
    },
    validityLabel() {
      if (this.verificationData?.subscription?.is_renewal_date) {
        return this.$t("SubscriptionVerify.si_rinnova_il");
      }
      return this.$t("SubscriptionVerify.valido_fino");
    },
    statusClass() {
      if (!this.verificationData) return "notfound";
      return this.verificationData.status === "valid" ? "valid" 
           : this.verificationData.status === "pending" ? "pending"
           : this.verificationData.status === "expired" ? "expired" 
           : "notfound";
    },
    statusIcon() {
      const icons = {
        valid: "check-circle-fill",
        pending: "hourglass-split",
        expired: "exclamation-circle-fill",
        notfound: "x-circle-fill"
      };
      return icons[this.statusClass];
    },
    statusTitle() {
      const titles = {
        valid: this.$t("SubscriptionVerify.valido_titolo"),
        pending: this.$t("SubscriptionVerify.pending_titolo"),
        expired: this.$t("SubscriptionVerify.scaduto_titolo"),
        notfound: this.$t("SubscriptionVerify.non_trovato_titolo")
      };
      return titles[this.statusClass];
    },
    statusSubtitle() {
      const subtitles = {
        valid: this.$t("SubscriptionVerify.valido_sottotitolo"),
        pending: this.$t("SubscriptionVerify.pending_sottotitolo"),
        expired: this.$t("SubscriptionVerify.scaduto_sottotitolo"),
        notfound: this.$t("SubscriptionVerify.non_trovato_sottotitolo")
      };
      return subtitles[this.statusClass];
    },
    badgeIcon() {
      return this.statusClass === "valid" ? "patch-check-fill" 
           : this.statusClass === "pending" ? "hourglass-split"
           : this.statusClass === "expired" ? "exclamation-triangle-fill"
           : "x-circle-fill";
    },
    badgeText() {
      const texts = {
        valid: this.$t("SubscriptionVerify.verificato"),
        pending: this.$t("SubscriptionVerify.in_attivazione"),
        expired: this.$t("SubscriptionVerify.scaduto_titolo"),
        notfound: this.$t("SubscriptionVerify.non_trovato_titolo")
      };
      return texts[this.statusClass];
    }
  },
  methods: {
    formatDate(date) {
      if (!date) return "";
      return moment(date).format("DD MMMM YYYY");
    },
    async verifySubscription() {
      const hashid = this.$route.params.hashid;
      if (!hashid) {
        this.loading = false;
        return;
      }

      try {
        const response = await axios.get(
          `${config.apiUrl}subscriptions/${hashid}/verify`
        );
        this.verificationData = response.data.data;
      } catch (err) {
        this.verificationData = {
          status: "not_found",
          valid: false,
        };
      } finally {
        this.loading = false;
      }
    },
  },
  created() {
    this.verifySubscription();
  },
};
</script>

<style scoped lang="scss">
.verify-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem 1rem;
}

.verify-content {
  width: 100%;
  max-width: 440px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.logo-section {
  margin-bottom: 2rem;
  
  .brand-logo {
    height: 70px;
    width: auto;
    object-fit: contain;
  }
}

.verify-card {
  width: 100%;
  background: #fff;
  border-radius: 24px;
  padding: 2.5rem 2rem;
  text-align: center;
  box-shadow: 
    0 25px 50px -12px rgba(0, 0, 0, 0.25),
    0 0 0 1px rgba(255, 255, 255, 0.1);
  
  &.loading-card {
    padding: 3rem 2rem;
  }

  &.valid {
    border-top: 5px solid #22c55e;
  }

  &.pending {
    border-top: 5px solid #f59e0b;
  }

  &.expired {
    border-top: 5px solid #f97316;
  }

  &.notfound {
    border-top: 5px solid #ef4444;
  }
}

.status-icon {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;
  color: #fff;

  &.valid {
    background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
    box-shadow: 0 8px 24px rgba(34, 197, 94, 0.4);
  }

  &.pending {
    background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
    box-shadow: 0 8px 24px rgba(245, 158, 11, 0.4);
    color: #fff;
  }

  &.expired {
    background: linear-gradient(135deg, #f97316 0%, #ea580c 100%);
    box-shadow: 0 8px 24px rgba(249, 115, 22, 0.4);
  }

  &.notfound {
    background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
    box-shadow: 0 8px 24px rgba(239, 68, 68, 0.4);
  }
}

.status-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: $body-text-color;
  margin-bottom: 0.5rem;
  line-height: 1.3;
}

.status-subtitle {
  font-size: 0.95rem;
  color: $font-grey;
  margin-bottom: 1.5rem;
  line-height: 1.5;
}

.details-section {
  background: $extra-light;
  border-radius: 16px;
  padding: 1rem 1.25rem;
  margin-bottom: 1.5rem;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 0;
  border-bottom: 1px solid $line-grey;

  &:last-child {
    border-bottom: none;
  }
}

.detail-label {
  font-size: 0.875rem;
  color: $font-grey;
}

.detail-value {
  font-size: 0.9rem;
  font-weight: 600;
  color: $body-text-color;
  text-align: right;

  &.text-warning {
    color: #f97316;
  }
}

.status-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.625rem 1.5rem;
  border-radius: 50px;
  font-weight: 600;
  font-size: 0.9rem;
  color: #fff;

  &.valid {
    background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
  }

  &.pending {
    background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  }

  &.expired {
    background: linear-gradient(135deg, #f97316 0%, #ea580c 100%);
  }

  &.notfound {
    background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  }
}

.footer-text {
  margin-top: 1.5rem;
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.6);
}

@media (max-width: 480px) {
  .verify-page {
    padding: 1.5rem 1rem;
  }

  .verify-card {
    padding: 2rem 1.5rem;
    border-radius: 20px;
  }

  .status-title {
    font-size: 1.3rem;
  }

  .detail-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.25rem;
  }

  .detail-value {
    text-align: left;
  }
}
</style>
